import React, { useEffect, useState } from 'react';
import { DashboardLayout } from './layouts/DashboardLayout';
import { Dashboard } from './pages/Dashboard';
import { Realtime } from './pages/Realtime';
import { ModelManagement } from './pages/ModelManagement';
import { Guardrails } from './pages/Guardrails';
import { AccessControl } from './pages/AccessControl';
import { ApiSystem } from './pages/ApiSystem';
import { PromptMonitoring } from './pages/PromptMonitoring';
import { GpuOverview } from './pages/GpuOverview';
import { AppMarketplace } from './pages/AppMarketplace';
import { WorkflowBuilder } from './pages/WorkflowBuilder';
import { ChatConsole } from './pages/ChatConsole';
import { CostIntelligence } from './pages/CostIntelligence';
import { Login } from './pages/Login';
import { auth } from './utils/auth';
interface AuthUser {
  username: string;
  name: string;
  role: string;
}
export function App() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [lockoutTime, setLockoutTime] = useState<number | null>(null);
  // Set document title
  useEffect(() => {
    document.title = 'Ghisha Portal';
  }, []);
  // Check if user is already logged in
  useEffect(() => {
    const currentUser = auth.getCurrentUser();
    setUser(currentUser);
    // Check login status for lockout
    const status = auth.getLoginStatus();
    if (status.locked && status.lockoutTime) {
      setLockoutTime(status.lockoutTime);
    } else if (status.remainingAttempts) {
      setRemainingAttempts(status.remainingAttempts);
    }
    setIsLoading(false);
  }, []);
  // Simply update the current page without additional loading state
  const handlePageChange = (page: string) => {
    setCurrentPage(page);
  };
  // Handle login
  const handleLogin = (username: string, password: string) => {
    const result = auth.login(username, password);
    if (result.success) {
      const currentUser = auth.getCurrentUser();
      setUser(currentUser);
      setLoginError(null);
      setRemainingAttempts(null);
    } else {
      setLoginError(result.message || 'Login failed');
      if (result.remainingAttempts !== undefined) {
        setRemainingAttempts(result.remainingAttempts);
      }
      // Check if account is now locked
      const status = auth.getLoginStatus();
      if (status.locked && status.lockoutTime) {
        setLockoutTime(status.lockoutTime);
      }
    }
  };
  // Handle logout
  const handleLogout = () => {
    auth.logout();
    setUser(null);
    setCurrentPage('dashboard');
  };
  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard data-id="element-0" />;
      case 'realtime':
        return <Realtime data-id="element-1" />;
      case 'models':
        return <ModelManagement data-id="element-2" />;
      case 'costs':
        return <CostIntelligence data-id="element-3" />;
      case 'guardrails':
        return <Guardrails data-id="element-4" />;
      case 'access':
        return <AccessControl data-id="element-5" />;
      case 'prompts':
        return <PromptMonitoring data-id="element-6" />;
      case 'gpu':
        return <GpuOverview data-id="element-7" />;
      case 'marketplace':
        return <AppMarketplace data-id="element-8" />;
      case 'workflow':
        return <WorkflowBuilder data-id="element-9" />;
      case 'chat':
        return <ChatConsole data-id="element-10" />;
      default:
        return <Dashboard data-id="element-11" />;
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center h-screen" data-id="element-12">
        Loading...
      </div>;
  }
  // If not logged in, show login page
  if (!user) {
    return <Login onLogin={handleLogin} loginError={loginError} remainingAttempts={remainingAttempts} lockoutTime={lockoutTime} data-id="element-13" />;
  }
  return <DashboardLayout currentPage={currentPage} setCurrentPage={handlePageChange} user={user} onLogout={handleLogout} data-id="element-14">
      {renderPage()}
    </DashboardLayout>;
}